import React, { useState, useEffect } from "react";
import { Table, message, Avatar, Tag } from "antd";
import moment from "moment";
import { platformApi } from "../../../../api";
import LedgerModal from "../LedgerModal";

const LedgerTable = ({
  type,
  pageSize,
  searchString,
  currentPage,
  setCurrentPage,
  count,
  setCount,
  activeTab,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const typeToTabKey = {
    BANK_ACCOUNT: "1",
    CASH_ACCOUNT: "2",
    CUSTOMERS: "3",
    EMPLOYEES: "4",
    SUPPLIERS: "5",
    PINELABS_ACCOUNT: "6",
  };

  useEffect(() => {
    if (activeTab === typeToTabKey[type]) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, pageSize, searchString, currentPage, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (type === "BANK_ACCOUNT" || type === "CASH_ACCOUNT") {
        const payload = { size: pageSize, searchString, page: currentPage };
        const res = await platformApi.post("/api/company/branches/get", payload);
        if (res.data.code === 200) {
          const branches = res.data.response.data.branch;
          const totalCount = res.data.response.data.count;
          if (type === "BANK_ACCOUNT") {
            const flattenedData = [];
            branches.forEach((branch) => {
              if (branch.bankDetails?.length) {
                branch.bankDetails.forEach((bank) => {
                  flattenedData.push({
                    ...bank,
                    branchName: branch.name,
                    key: bank.id,
                  });
                });
              }
            });
            setData(flattenedData);
            setCount(flattenedData.length);
          } else {
            setData(branches.map((b) => ({ ...b, key: b.id })));
            setCount(totalCount);
          }
        }
      } else if (type === "CUSTOMERS") {
        const payload = { page: currentPage, size: pageSize, searchString };
        const res = await platformApi.post("/api/customer/get", payload);
        if (res.data.code === 200) {
          setData(res.data.response.data.customer.map((c) => ({ ...c, key: c.id })));
          setCount(res.data.response.data.count);
        }
      } else if (type === "EMPLOYEES") {
        const payload = { searchString };
        const res = await platformApi.post("/api/user/get", payload);
        if (res.data.code === 200) {
          const users = res.data.response.data.users
            .filter((u) => u.status === true)
            .map((u) => ({ ...u, key: u.id }));
          setData(users);
          setCount(users.length);
        }
      } else if (type === "SUPPLIERS") {
        const payload = { page: currentPage, size: pageSize, searchString };
        const res = await platformApi.post("/api/supplier/get", payload);
        if (res.data.code === 200) {
          setData(res.data.response.data.supplier.map((s) => ({ ...s, key: s.id })));
          setCount(res.data.response.data.count);
        }
      } else if (type === "PINELABS_ACCOUNT") {
        const payload = { searchString, page: currentPage, size: pageSize };
        const res = await platformApi.post("/api/pinelabs/get", payload);
        if (res.data.status === 200 || res.data.code === 200) {
          const records = res.data.response?.data?.records || [];
          setData(records.map((r) => ({ ...r, key: r.id })));
          setCount(res.data.response?.data?.count || records.length);
        }
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const getColumns = () => {
    switch (type) {
      case "BANK_ACCOUNT":
        return [
          { title: "Name", dataIndex: "name", key: "name" },
          { title: "Account", dataIndex: "accountNumber", key: "accountNumber" },
          { title: "Account Name", dataIndex: "accountName", key: "accountName" },
          { title: "IFSC", dataIndex: "ifsc", key: "ifsc" },
          { title: "Branch", dataIndex: "branchName", key: "branchName" },
          { title: "Account Balance (₹)", dataIndex: "accountBalance", key: "accountBalance" },
          {
            title: "Action",
            key: "action",
            render: (_, record) => (
              <span className="linkylink" onClick={() => { setSelectedItem(record); setModalVisible(true); }}>
                View
              </span>
            ),
          },
        ];
      case "CASH_ACCOUNT":
        return [
          { title: "Branch Name", dataIndex: "name", key: "name" },
          { title: "Email", dataIndex: "email", key: "email" },
          { title: "Branch Type", dataIndex: "branchType", key: "branchType" },
          {
            title: "Phone",
            dataIndex: "contacts",
            key: "phone",
            render: (contacts) => (contacts?.[0]?.phone || "-"),
          },
          { title: "Account Balance (₹)", dataIndex: "accountBalance", key: "accountBalance" },
          {
            title: "Action",
            key: "action",
            render: (_, record) => (
              <span className="linkylink" onClick={() => { setSelectedItem(record); setModalVisible(true); }}>
                View
              </span>
            ),
          },
        ];
      case "CUSTOMERS":
        return [
          { title: "Customer Name", dataIndex: "name", key: "name" },
          {
            title: "Mobile Number",
            dataIndex: "contacts",
            key: "phone",
            render: (contacts) => (
              <div>
                {contacts?.length
                  ? contacts.map((c, i) => <div key={i}>{c.phone}</div>)
                  : "-"}
              </div>
            ),
          },
          {
            title: "Locality",
            dataIndex: "address",
            key: "locality",
            render: (addr) => <span>{addr?.locality || "-"}</span>,
          },
          {
            title: "DOB",
            dataIndex: "dateOfBirth",
            key: "dob",
            render: (date) => <span>{date ? moment(date).format("DD-MM-YYYY") : "-"}</span>,
          },
          { title: "Grouping", dataIndex: "customerGrouping", key: "grouping" },
          { title: "Type", dataIndex: "customerType", key: "type" },
          {
            title: "Created On",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => <span>{date ? moment(date).format("DD-MM-YYYY") : "-"}</span>,
          },
          {
            title: "Action",
            key: "action",
            render: (_, record) => (
              <span className="linkylink" onClick={() => { setSelectedItem(record); setModalVisible(true); }}>
                View
              </span>
            ),
          },
        ];
      case "EMPLOYEES":
        return [
          {
            title: "Profile Picture",
            dataIndex: "profilePicture",
            key: "image",
            render: (url) => <Avatar icon={!url && "user"} src={url} size={48} />,
          },
          {
            title: "Name",
            dataIndex: "profile",
            key: "name",
            render: (profile) => <span>{profile?.employeeName || "-"}</span>,
          },
          { title: "Mobile Number", dataIndex: "phone", key: "phone" },
          { title: "Alternate Number", dataIndex: "phone2", key: "alt" },
          {
            title: "Branch",
            dataIndex: "profile",
            key: "branch",
            render: (p) => p?.branch?.map((b) => <Tag color="#006a71" key={b.id}>{b.name}</Tag>),
          },
          {
            title: "Department",
            dataIndex: "profile",
            key: "dept",
            render: (p) => <span>{p?.department?.role || "-"}</span>,
          },
          {
            title: "Joined On",
            dataIndex: "profile.dateOfJoining",
            key: "join",
            render: (text) => <span>{text ? moment(text).format("DD-MM-YYYY") : "-"}</span>,
          },
          {
            title: "Action",
            key: "action",
            render: (_, record) => (
              <span className="linkylink" onClick={() => { setSelectedItem(record); setModalVisible(true); }}>
                View
              </span>
            ),
          },
        ];
      case "SUPPLIERS":
        return [
          { title: "Supplier Name", dataIndex: "name", key: "name" },
          {
            title: "Supplier Type",
            dataIndex: "supplierType",
            key: "type",
            render: (types) => types?.map((t, i) => <Tag color="blue" key={i}>{t}</Tag>),
          },
          { title: "Status", dataIndex: "status", key: "status" },
          {
            title: "City",
            dataIndex: "address",
            key: "city",
            render: (a) => <span>{a?.district?.name || "-"}</span>,
          },
          {
            title: "GST D.Type",
            dataIndex: "dealerType",
            key: "dealerType",
            render: (text) => (
              <span>
                {text === "RegisteredDealer" ? "Registered Dealer" :
                 text === "UnregisteredDealer" ? "Unregistered Dealer" :
                 text === "UINHolder" ? "UIN Holder" : "Composition Dealer"}
              </span>
            ),
          },
          {
            title: "Action",
            key: "action",
            render: (_, record) => (
              <span className="linkylink" onClick={() => { setSelectedItem(record); setModalVisible(true); }}>
                View
              </span>
            ),
          },
        ];
      case "PINELABS_ACCOUNT":
        return [
          { title: "Machine ID", dataIndex: "machineId", key: "machineId" },
          { title: "Branch", dataIndex: "branch", key: "branch", render: (b) => b?.name || "-" },
          { title: "Client ID", dataIndex: "clientId", key: "clientId" },
          { title: "Merchant ID", dataIndex: "merchantId", key: "merchantId" },
          { title: "Account Balance (₹)", dataIndex: "accountBalance", key: "accountBalance" },
          {
            title: "Action",
            key: "action",
            render: (_, record) => (
              <span className="linkylink" onClick={() => { setSelectedItem(record); setModalVisible(true); }}>
                View
              </span>
            ),
          },
        ];
      default:
        return [];
    }
  };

  return (
    <div>
      <Table
        loading={loading}
        columns={getColumns()}
        dataSource={data}
        pagination={{
          current: currentPage,
          pageSize,
          total: count,
          onChange: (page) => setCurrentPage(page),
        }}
      />
      <LedgerModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        data={selectedItem}
        type={type}
      />
    </div>
  );
};

export default LedgerTable;
